import { FC, useState } from "react";
import { Button, Descriptions, Card, Select, message } from "antd";
import { ArrowRight, Zap, FileText, Calendar, User, FolderOpen } from "lucide-react";
import dayjs from "dayjs";
import { useEstimateDetailStore } from "../estimate-detail.store";
import { EstimateStatus, EstimateTicket } from "~/types/estimate";
import { useGetProjects } from "~/services/project/project.queries";
import { useUpdateEstimate } from "~/services/estimate";

interface OverviewTabProps {
  estimate?: EstimateTicket;
}

export const OverviewTab: FC<OverviewTabProps> = ({ estimate }) => {
  const { setShowConvertModal } = useEstimateDetailStore();
  const [isEditingProject, setIsEditingProject] = useState(false);

  // Fetch projects for dropdown
  const { data: projectsData } = useGetProjects({ pageSize: 100 });
  const projects = projectsData?.items || [];

  // Update estimate mutation
  const updateEstimateMutation = useUpdateEstimate();

  if (!estimate) return null;

  // Handle project change
  const handleProjectChange = async (projectId: string | null) => {
    try {
      await updateEstimateMutation.mutateAsync({
        id: estimate.id,
        data: { projectId: projectId || undefined },
      });
      message.success(projectId ? "Project assigned - Expo mode enabled" : "Project removed");
      setIsEditingProject(false);
    } catch (error: any) {
      message.error(error?.response?.data?.message || "Failed to update project");
    }
  };

  const isExpoMode = !!estimate.projectId;

  const getClientInfo = () => {
    if (estimate.tempClientData) {
      return {
        name: estimate.tempClientData.name,
        email: estimate.tempClientData.email || 'N/A',
        phone: estimate.tempClientData.phone || 'N/A',
        isTemp: true,
      };
    }
    if (estimate.client) {
      return {
        name: estimate.client.companyName ||
              `${estimate.client.firstName} ${estimate.client.lastName}`.trim() ||
              "Unnamed Client",
        email: estimate.client.email || 'N/A',
        phone: estimate.client.contactPhone || 'N/A',
        isTemp: false,
      };
    }
    return { name: 'N/A', email: 'N/A', phone: 'N/A', isTemp: false };
  };

  const clientInfo = getClientInfo();
  const canConvert = estimate.status !== EstimateStatus.CONVERTED;

  return (
    <div className="space-y-6">
      {/* Convert to Booking Banner */}
      {canConvert && (
        <Card className="bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-100 p-3 rounded-full">
                <ArrowRight className="text-blue-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900">Ready to convert this estimate?</h3>
                <p className="text-sm text-blue-700">
                  Convert this estimate to a full booking with scheduled service tickets
                </p>
              </div>
            </div>
            <Button
              type="primary"
              size="large"
              icon={<ArrowRight size={16} />}
              onClick={() => setShowConvertModal(true)}
              className="bg-blue-600"
            >
              Convert to Booking
            </Button>
          </div>
        </Card>
      )}

      {/* Converted Status */}
      {estimate.status === EstimateStatus.CONVERTED && estimate.convertedToJobTicketId && (
        <Card className="bg-purple-50 border-purple-200">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-3 rounded-full">
              <FileText className="text-purple-600" size={24} />
            </div>
            <div>
              <h3 className="font-semibold text-purple-900">Converted to Booking</h3>
              <p className="text-sm text-purple-700">
                This estimate has been converted to booking
              </p>
            </div>
            <Button
              type="link"
              href={`/bookings/${estimate.convertedToJobTicketId}`}
              className="ml-auto"
            >
              View Booking
            </Button>
          </div>
        </Card>
      )}

      {/* Expo Mode Banner */}
      {isExpoMode && (
        <Card className="bg-purple-50 border-purple-200">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-3 rounded-full">
              <FolderOpen className="text-purple-600" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-purple-900">Expo Mode Enabled</h3>
              <p className="text-sm text-purple-700">
                Project: <strong>{estimate.project?.name}</strong> - Multi-day service scheduling available for On-Site Services
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Estimate Information */}
        <Card title="Estimate Information" className="shadow-sm">
          <Descriptions column={1} size="small">
            <Descriptions.Item label={<span className="text-gray-600">Reference Number</span>}>
              <span className="font-medium">{estimate.referenceNumber}</span>
            </Descriptions.Item>
            <Descriptions.Item label={<span className="text-gray-600">Estimate Date</span>}>
              {dayjs(estimate.estimateDate).format("MMM DD, YYYY")}
            </Descriptions.Item>
            <Descriptions.Item label={<span className="text-gray-600">Status</span>}>
              <span className="capitalize">{estimate.status.toLowerCase()}</span>
            </Descriptions.Item>
            <Descriptions.Item label={<span className="text-gray-600">Created</span>}>
              {dayjs(estimate.createdAt).format("MMM DD, YYYY h:mm A")}
            </Descriptions.Item>
            <Descriptions.Item label={<span className="text-gray-600">Last Updated</span>}>
              {dayjs(estimate.updatedAt).format("MMM DD, YYYY h:mm A")}
            </Descriptions.Item>
          </Descriptions>

          {estimate.notes && (
            <div className="mt-4 pt-4 border-t">
              <div className="text-sm text-gray-600 mb-2">Notes</div>
              <div className="text-sm">{estimate.notes}</div>
            </div>
          )}
        </Card>

        {/* Client Information */}
        <Card
          title={
            <div className="flex items-center space-x-2">
              <span>Client Information</span>
              {clientInfo.isTemp && (
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                  Temporary
                </span>
              )}
            </div>
          }
          className="shadow-sm"
        >
          <Descriptions column={1} size="small">
            <Descriptions.Item label={<span className="text-gray-600">Name</span>}>
              <span className="font-medium">{clientInfo.name}</span>
            </Descriptions.Item>
            <Descriptions.Item label={<span className="text-gray-600">Email</span>}>
              {clientInfo.email}
            </Descriptions.Item>
            <Descriptions.Item label={<span className="text-gray-600">Phone</span>}>
              {clientInfo.phone}
            </Descriptions.Item>
          </Descriptions>

          {clientInfo.isTemp && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-start space-x-2 text-sm text-orange-700 bg-orange-50 p-3 rounded">
                <Zap size={16} className="mt-0.5" />
                <div>
                  <div className="font-medium">Temporary Contact</div>
                  <div className="text-xs">
                    When converting to booking, you'll be prompted to create a full client record.
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Project Assignment */}
        <Card
          title={
            <div className="flex items-center space-x-2">
              <FolderOpen size={16} />
              <span>Project Assignment</span>
              {isExpoMode && (
                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  Expo Mode
                </span>
              )}
            </div>
          }
          className="shadow-sm"
          extra={
            estimate.status !== EstimateStatus.CONVERTED && (
              <Button
                type="link"
                size="small"
                onClick={() => setIsEditingProject(!isEditingProject)}
              >
                {isEditingProject ? "Cancel" : "Change"}
              </Button>
            )
          }
        >
          {isEditingProject ? (
            <div className="space-y-3">
              <Select
                placeholder="Select a project (enables Expo Mode)"
                value={estimate.projectId || undefined}
                onChange={handleProjectChange}
                allowClear
                showSearch
                loading={updateEstimateMutation.isPending}
                className="w-full"
                size="large"
                filterOption={(input, option) =>
                  (option?.label?.toString() ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={projects.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
              />
              <div className="text-xs text-gray-500">
                Assigning a project enables Expo Mode for multi-day On-Site Service scheduling
              </div>
            </div>
          ) : (
            <>
              {estimate.project ? (
                <Descriptions column={1} size="small">
                  <Descriptions.Item label={<span className="text-gray-600">Project Name</span>}>
                    <span className="font-medium">{estimate.project.name}</span>
                  </Descriptions.Item>
                  {estimate.project.description && (
                    <Descriptions.Item label={<span className="text-gray-600">Description</span>}>
                      {estimate.project.description}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              ) : (
                <div className="text-center py-4 text-gray-500">
                  <FolderOpen size={32} className="mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">No project assigned</p>
                  <p className="text-xs">Assign a project to enable Expo Mode</p>
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* Services Summary */}
      <Card title={`Services (${estimate.services?.length || 0})`} className="shadow-sm">
        {estimate.services && estimate.services.length > 0 ? (
          <div className="space-y-3">
            {estimate.services.map((service) => (
              <div key={service.id} className="border rounded p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-medium capitalize">
                      {service.serviceType.toLowerCase().replace('_', ' ')}
                    </div>
                    <div className="text-sm text-gray-500 mt-1 space-y-1">
                      {service.serviceDate && (
                        <div className="flex items-center space-x-2">
                          <Calendar size={14} />
                          <span>{dayjs(service.serviceDate).format("MMM DD, YYYY")}</span>
                        </div>
                      )}
                      {service.duration && <div>Duration: {service.duration} hours</div>}
                      {service.staffCount && <div>Staff: {service.staffCount}</div>}
                      {service.vessel && (
                        <div>Vessel: {service.vessel.name}</div>
                      )}
                    </div>
                  </div>
                  {service.estimatedPrice && (
                    <div className="text-lg font-semibold">
                      ${service.estimatedPrice.toFixed(2)}
                    </div>
                  )}
                </div>
                {service.notes && (
                  <div className="mt-2 pt-2 border-t text-sm text-gray-600">
                    {service.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <FileText size={48} className="mx-auto mb-3 text-gray-300" />
            <p>No services added yet</p>
            <Button type="link" onClick={() => {/* TODO: open add service */}}>
              Add Service
            </Button>
          </div>
        )}
      </Card>

      {/* Estimated Total */}
      {estimate.estimatedTotal && (
        <Card className="shadow-sm bg-gray-50">
          <div className="flex justify-between items-center">
            <span className="text-lg font-medium">Estimated Total</span>
            <span className="text-2xl font-bold text-green-600">
              ${estimate.estimatedTotal.toFixed(2)}
            </span>
          </div>
        </Card>
      )}
    </div>
  );
};
